import { ReactElement, useState } from 'react';
import { useRouter } from 'next/router';
import { Link, useOverlayState } from '@heroui/react';
import { ThemeSwitch } from '@components/main/theme-switch';
import { useSession } from '@context/sessionProvider';
import PlansModal from '@components/plans/plans-modal';
import { HomeIcon } from '@icons/home';
import { BookOpenIcon } from '@icons/bookOpenIcon';

const NAVIGATION_PANEL = [
	{ label: 'Timers', href: '/', Icon: HomeIcon },
	{ label: 'User guide', href: '/help', Icon: BookOpenIcon },
];

export default function Navigation(): ReactElement {
	const router = useRouter();
	const sessionData = useSession();
	const { pathname } = router;

	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const plansModalState = useOverlayState();

	const closeMenu = () => setIsMenuOpen(false);
	const openPlansModal = () => plansModalState.open();

	return (
		<>
			<nav className='relative z-40 border-[#DCDCDC] border-y bg-background px-[40px] py-[7.5px]'>
				<header className='flex min-h-[40px] items-center justify-between'>
					<button
						type='button'
						aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
						aria-expanded={isMenuOpen}
						onClick={() => setIsMenuOpen((isOpen) => !isOpen)}
						className='flex h-10 w-10 flex-col items-center justify-center gap-1.5 text-foreground lg:hidden'
					>
						<span className='h-0.5 w-5 rounded-full bg-current' />
						<span className='h-0.5 w-5 rounded-full bg-current' />
						<span className='h-0.5 w-5 rounded-full bg-current' />
					</button>
					<ul className='hidden gap-4 lg:flex'>
						{NAVIGATION_PANEL.map(({ Icon, href }) => (
							<li key={`${href}`} className='flex h-[40px] items-center'>
								<Link
									href={href}
									aria-label={href}
									aria-current={pathname === href ? 'page' : undefined}
									className='flex items-center'
								>
									<Icon />
								</Link>
							</li>
						))}
					</ul>
					<div className='flex items-center gap-4'>
						<div className='hidden lg:flex'>
							<ThemeSwitch />
						</div>
						<div className='hidden lg:flex'>
							<button
								type='button'
								title='Click to view pricing plans'
								onClick={openPlansModal}
								className='rounded-xl bg-[#006FEE] px-3 py-[4px] text-[#ffffff] transition-opacity hover:opacity-90'
							>
								<p className='font-lato text-[14px] font-medium leading-[20px]'>
									Current plan: <span>{sessionData.user.planName}</span>
								</p>
							</button>
						</div>
					</div>
				</header>
				{isMenuOpen && (
					<div className='absolute top-full right-0 left-0 z-50 border-[#DCDCDC] border-t bg-background py-8 lg:hidden'>
						<ul className='flex flex-col gap-6 px-[40px]'>
							{NAVIGATION_PANEL.map(({ label, href }) => (
								<li key={`${href}`} className='text-lg font-medium'>
									<Link
										href={href}
										onPress={closeMenu}
										aria-current={pathname === href ? 'page' : undefined}
										className={`block w-full ${pathname === href ? 'text-[#006FEE]' : 'text-foreground'}`}
									>
										{label}
									</Link>
								</li>
							))}
							<li>
								<hr className='border-[#DCDCDC]' />
							</li>
							<li className='flex flex-row items-center justify-between'>
								<span className='text-sm font-medium'>Theme</span>
								<ThemeSwitch />
							</li>
							<li>
								<button
									type='button'
									onClick={() => {
										openPlansModal();
										closeMenu();
									}}
									className='w-full rounded-xl bg-[#006FEE] px-4 py-2 text-center text-[#ffffff]'
								>
									<p className='text-[14px]'>Current plan: {sessionData.user.planName}</p>
								</button>
							</li>
						</ul>
					</div>
				)}
			</nav>
			<PlansModal
				isOpen={plansModalState.isOpen}
				isFree={sessionData.user.isFree}
				onOpenChange={plansModalState.setOpen}
			/>
		</>
	);
}
